import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useMediaQuery } from 'react-responsive';
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Container,
  FormControl, Grid, InputLabel, MenuItem, Paper, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';

// Mendo fiscal year starts on the Monday closest to Dec 29.
function fiscalYearStart(year) {
  const dec29 = new Date(year - 1, 11, 29);
  const dow = dec29.getDay(); // 0=Sun..6=Sat
  // Shift to Monday closest to Dec 29.
  // Mondays before/after Dec 29 — pick the nearer.
  const back = (dow + 6) % 7;  // days back to previous Monday
  const fwd = (8 - dow) % 7;   // days forward to next Monday
  const shift = back <= fwd ? -back : fwd;
  const d = new Date(dec29);
  d.setDate(d.getDate() + shift);
  return d;
}

function fiscalYtd() {
  const now = new Date();
  // If we're between Jan 1 and the fiscal-year start that happens around Dec 28,
  // the YTD range starts in the previous calendar year.
  const start = fiscalYearStart(now.getFullYear());
  const effectiveStart = now < start ? fiscalYearStart(now.getFullYear() - 1) : start;
  return { from: effectiveStart, to: now };
}

function priorFiscalYear() {
  const now = new Date();
  const currentFyStart = now < fiscalYearStart(now.getFullYear())
    ? fiscalYearStart(now.getFullYear() - 1)
    : fiscalYearStart(now.getFullYear());
  const priorFyStart = fiscalYearStart(currentFyStart.getFullYear());
  const priorFyEnd = new Date(currentFyStart);
  priorFyEnd.setDate(priorFyEnd.getDate() - 1);
  return { from: priorFyStart, to: priorFyEnd };
}

// "Current fiscal quarter" — Mendo's fiscal quarters are 13 weeks (91 days)
// from fiscal year start. Simplest safe implementation: current fiscal month
// to today uses date-arithmetic; current fiscal quarter uses the 91-day
// bucket we're in since the fiscal start.
function currentFiscalMonth() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from, to: now };
}

function currentFiscalQuarter() {
  const now = new Date();
  const fyStart = now < fiscalYearStart(now.getFullYear())
    ? fiscalYearStart(now.getFullYear() - 1)
    : fiscalYearStart(now.getFullYear());
  const daysSinceStart = Math.floor((now - fyStart) / (24 * 60 * 60 * 1000));
  const quarterIdx = Math.min(3, Math.floor(daysSinceStart / 91));
  const from = new Date(fyStart);
  from.setDate(from.getDate() + quarterIdx * 91);
  return { from, to: now };
}

function fmtMoney(n) {
  const v = Number(n) || 0;
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export default function DeliveriesByZipcode() {
  const isTabletOrMobile = useMediaQuery({ maxWidth: 900 });
  const { user: firebaseUser } = useUserAuth();

  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [selectedStores, setSelectedStores] = useState([]);

  const defaultRange = useMemo(fiscalYtd, []);
  const [dateFrom, setDateFrom] = useState(defaultRange.from);
  const [dateTo, setDateTo] = useState(defaultRange.to);
  const [preset, setPreset] = useState('fiscal-ytd');

  const [previewing, setPreviewing] = useState(false);
  const [exporting, setExporting] = useState(null); // 'xlsx' | 'csv' | null
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // { previewId, totalRows, perStore, sample, ... }

  const getToken = useCallback(async () => firebaseUser?.getIdToken?.(true), [firebaseUser]);

  // Load stores on mount.
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const { data } = await axios.get('/hd-api/deliveries-by-zipcode/stores', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStores(data?.stores || []);
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      } finally {
        setStoresLoading(false);
      }
    })();
  }, [getToken]);

  // Preset changes update the dates. 'custom' leaves them alone.
  useEffect(() => {
    let range = null;
    if (preset === 'fiscal-ytd') range = fiscalYtd();
    else if (preset === 'current-month') range = currentFiscalMonth();
    else if (preset === 'current-quarter') range = currentFiscalQuarter();
    else if (preset === 'prior-fy') range = priorFiscalYear();
    if (range) {
      setDateFrom(range.from);
      setDateTo(range.to);
    }
  }, [preset]);

  const canPreview = selectedStores.length > 0 && dateFrom && dateTo && dateFrom <= dateTo;

  async function handlePreview() {
    setPreviewing(true);
    setError(null);
    setPreview(null);
    try {
      const token = await getToken();
      const { data } = await axios.post(
        '/hd-api/deliveries-by-zipcode/preview',
        {
          storeNumbers: selectedStores.map((s) => s.storeNumber),
          dateFrom: format(dateFrom, 'yyyy-MM-dd'),
          dateTo: format(dateTo, 'yyyy-MM-dd'),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreview(data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setPreviewing(false);
    }
  }

  async function handleDownload(fmt) {
    setExporting(fmt);
    setError(null);
    try {
      const token = await getToken();
      const resp = await axios.post(
        `/hd-api/deliveries-by-zipcode/export?format=${fmt}`,
        {
          storeNumbers: selectedStores.map((s) => s.storeNumber),
          dateFrom: format(dateFrom, 'yyyy-MM-dd'),
          dateTo: format(dateTo, 'yyyy-MM-dd'),
          previewId: preview?.previewId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 5 * 60 * 1000,
        }
      );
      // Extract filename from Content-Disposition.
      const disposition = resp.headers['content-disposition'] || '';
      const m = disposition.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `Deliveries_By_ZipCode.${fmt}`;

      const blob = new Blob([resp.data], { type: resp.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setExporting(null);
    }
  }

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Box sx={{
              backgroundColor: 'white',
              borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
              px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2,
            }}>
              <Box sx={{
                backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                borderRadius: 2, p: 1, display: 'flex', alignItems: 'center',
              }}>
                <LocalShippingIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato' }}>
                  Deliveries by Zip Code
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One row per delivery order across Catering + Core Dispatch channels.
                </Typography>
              </Box>
            </Box>
          </Paper>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  size="small"
                  options={stores}
                  loading={storesLoading}
                  getOptionLabel={(s) => `${s.storeNumber} — ${s.name}`}
                  isOptionEqualToValue={(a, b) => a.storeNumber === b.storeNumber}
                  value={selectedStores}
                  onChange={(_, v) => setSelectedStores(v)}
                  renderInput={(params) => (
                    <TextField {...params} label="Stores" placeholder="Pick one or more…" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        size="small"
                        label={`${option.storeNumber} — ${option.name}`}
                        {...getTagProps({ index })}
                        key={option.storeNumber}
                      />
                    ))
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date range preset</InputLabel>
                  <Select
                    value={preset}
                    label="Date range preset"
                    onChange={(e) => setPreset(e.target.value)}
                  >
                    <MenuItem value="fiscal-ytd">Fiscal YTD</MenuItem>
                    <MenuItem value="current-month">Current month</MenuItem>
                    <MenuItem value="current-quarter">Current fiscal quarter</MenuItem>
                    <MenuItem value="prior-fy">Prior fiscal year</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6} md={1.5}>
                <DatePicker
                  selected={dateFrom}
                  onChange={(d) => { setDateFrom(d); setPreset('custom'); }}
                  dateFormat="yyyy-MM-dd"
                  customInput={<TextField label="From" size="small" fullWidth />}
                />
              </Grid>
              <Grid item xs={6} md={1.5}>
                <DatePicker
                  selected={dateTo}
                  onChange={(d) => { setDateTo(d); setPreset('custom'); }}
                  dateFormat="yyyy-MM-dd"
                  customInput={<TextField label="To" size="small" fullWidth />}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
              <Button
                variant="contained"
                startIcon={previewing ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                onClick={handlePreview}
                disabled={!canPreview || previewing}
              >
                Preview
              </Button>
            </Box>
          </Paper>

          {preview && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <KpiCard label="Total rows" value={preview.totalRows.toLocaleString()} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard label="Net sales" value={fmtMoney(preview.totalNetSales)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard label="Check amount" value={fmtMoney(preview.totalCheckAmount)} />
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ mb: 2 }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle2" sx={{ flex: 1 }}>Per-store summary</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={exporting === 'csv' ? <CircularProgress size={14} /> : <FileDownloadIcon />}
                    disabled={!!exporting || !preview.totalRows}
                    onClick={() => handleDownload('csv')}
                  >
                    Download CSV
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={exporting === 'xlsx' ? <CircularProgress size={14} /> : <FileDownloadIcon />}
                    disabled={!!exporting || !preview.totalRows}
                    onClick={() => handleDownload('xlsx')}
                  >
                    Download XLSX
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Store</TableCell>
                        <TableCell align="right">Catering rows</TableCell>
                        <TableCell align="right">Catering net</TableCell>
                        <TableCell align="right">Dispatch rows</TableCell>
                        <TableCell align="right">Dispatch net</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.perStore.map((s) => (
                        <TableRow key={s.storeId} hover>
                          <TableCell>{s.storeId}{s.storeName ? ` — ${s.storeName}` : ''}</TableCell>
                          <TableCell align="right">{s.catering.rows.toLocaleString()}</TableCell>
                          <TableCell align="right">{fmtMoney(s.catering.netSales)}</TableCell>
                          <TableCell align="right">{s.coreDispatch.rows.toLocaleString()}</TableCell>
                          <TableCell align="right">{fmtMoney(s.coreDispatch.netSales)}</TableCell>
                        </TableRow>
                      ))}
                      {preview.perStore.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">No orders match the current filters.</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {preview.sample.length > 0 && (
                <Paper variant="outlined" sx={{ mb: 4 }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2">
                      Sample rows ({preview.sample.length} of {preview.totalRows.toLocaleString()})
                    </Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Store</TableCell>
                          <TableCell>Delivery date</TableCell>
                          <TableCell>Address</TableCell>
                          <TableCell>City</TableCell>
                          <TableCell>Zip</TableCell>
                          <TableCell align="right">Net sales</TableCell>
                          <TableCell align="right">Check amt</TableCell>
                          <TableCell>Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {preview.sample.map((r, i) => (
                          <TableRow key={i} hover>
                            <TableCell>{r.STOREID}</TableCell>
                            <TableCell>{r.DELIVERYDATE}</TableCell>
                            <TableCell>{r.DELIVERYADDRESS}</TableCell>
                            <TableCell>{r.CITY}</TableCell>
                            <TableCell>{r.DELIVERYZIPCODE}</TableCell>
                            <TableCell align="right">{fmtMoney(r.NETSALESTOTALS)}</TableCell>
                            <TableCell align="right">{fmtMoney(r.CHECKAMOUNTTOTALS)}</TableCell>
                            <TableCell>{r.ORDERTYPE}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </>
          )}
        </Box>
      </Container>
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2, mt: 0.5 }}>{value ?? '—'}</Typography>
    </Paper>
  );
}
